import './TQHelpOfferForm.scss';

import React, { useCallback, useMemo, useState } from 'react';

import { message } from 'antd';
import { useParams } from 'react-router-dom';

import CustomAvatar from '../../../../components/CustomAvatar/CustomAvatar';
import Header from '../../../../components/Header/Header';
import { uploadFilesInServiceRequest } from '../../../../context/thunks/tqConnectServiceRequestThunk';
import { useCreateTQServiceRequestResponse } from '../../../../hooks/mutations/tqConnectMutations/service-requests';
import { useTQConnectConfig } from '../../../../hooks/queries/tqConnectQueries';
import showAppError from '../../../../shared/error';
import { numFormatterInUnit } from '../../../../shared/formatter';
import {
  useAppDispatch,
  useAppNavigate,
  useAppSelector,
} from '../../../../shared/hooks';
import {
  EUploadModuleType,
  IFileAttachment,
} from '../../../../types/fileTypes';
import { ITQCreateServiceRequestResponseBody } from '../../../../types/tqConnectTypes/service-requests';
import AttachedFileButton from '../AttachedFileButton/AttachedFileButton';
import type { AttachedFileDisplay } from '../CreateServiceRequestForm/CreateServiceRequestForm';
import TQConnectBadge from '../TQConnectBadge';
import TQConnectIcon from '../TQConnectIcon';
import HelpOfferMessageEditor from './HelpOfferMessageEditor';

const PLACEHOLDER = 'Draft your offer message';

type FormStep = 'form' | 'message';

const block = 'tq-help-offer-form';

const TQHelpOfferForm: React.FC = () => {
  const navigate = useAppNavigate();
  const dispatch = useAppDispatch();
  const { serviceRequestId } = useParams<{ serviceRequestId: string }>();
  const userDetails = useAppSelector((state) => state.user);
  const { data: tqConnectConfigResult } = useTQConnectConfig();
  const scoreName =
    tqConnectConfigResult?.tqConnectConfig?.scoreConfig?.name ?? 'TQ';

  const [activeStep, setActiveStep] = useState<FormStep>('form');
  const [offerMessage, setOfferMessage] = useState('');
  const [attachedFiles, setAttachedFiles] = useState<AttachedFileDisplay[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);

  const { createServiceRequestResponse } = useCreateTQServiceRequestResponse(
    serviceRequestId || '',
  );

  const isFormValid = useMemo(
    () => offerMessage.trim().length > 0 && !isSubmitting,
    [offerMessage, isSubmitting],
  );

  const handleClose = useCallback(() => {
    navigate.goBack();
  }, [navigate]);

  const handleSubmit = useCallback(async () => {
    if (!isFormValid) return;
    setIsSubmitting(true);
    try {
      let uploadedFiles: IFileAttachment[] = [];

      if (attachedFiles.length > 0) {
        const resp = await dispatch(
          uploadFilesInServiceRequest(
            attachedFiles.map((file) => file.file),
            setIsUploading,
            (event) => {
              if (typeof event.progress === 'number') {
                setUploadProgress(event.progress);
              }
            },
            EUploadModuleType.TQ_CONNECT_SERVICE_REQUEST_RESPONSE,
          ),
        );

        if (resp && resp?.length > 0) {
          uploadedFiles = resp;
        }
      }

      const reqBody: ITQCreateServiceRequestResponseBody = {
        description: offerMessage,
        attachments: [...uploadedFiles],
      };
      createServiceRequestResponse(
        {
          serviceRequestId: serviceRequestId || '',
          data: reqBody,
        },
        {
          onSuccess: () => {
            message.success(`Sent your offer`);
          },
          onError: () => {
            setUploadProgress(0);
            setIsUploading(false);
          },
        },
      );
      navigate.goBack();
    } catch (error) {
      showAppError(error);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isFormValid,
    navigate,
    attachedFiles,
    dispatch,
    offerMessage,
    createServiceRequestResponse,
    serviceRequestId,
  ]);

  const tqScore = userDetails?.tqConnectScore ?? 0;
  const formattedScore = numFormatterInUnit(tqScore, 999);

  // Full-screen message editor step: save only on Done, cursor at end when description exists
  if (activeStep === 'message') {
    return (
      <HelpOfferMessageEditor
        initialMessage={offerMessage}
        onDone={(savedMessage) => {
          setOfferMessage(savedMessage);
          setActiveStep('form');
        }}
        onClose={() => setActiveStep('form')}
      />
    );
  }

  // Main form: tappable "How You Can Offer Help" row (like CreateServiceRequestForm title/description)
  return (
    <div className={`${block} page__container`}>
      <Header
        title="Offer Help"
        handleBack={handleClose}
        customHeaderClass={`${block}__header`}
        backType="cross"
        actionItems={[
          <button
            key="send"
            type="button"
            className={`${block}__header-btn ${
              isFormValid ? `${block}__header-btn--active` : ''
            }`}
            disabled={!isFormValid}
            onClick={handleSubmit}>
            {isUploading
              ? `Uploading... ${Math.round(uploadProgress)}%`
              : isSubmitting
                ? 'Sending...'
                : 'Send Offer to Help'}
          </button>,
        ]}
      />
      <div className={`${block}__scroll page__content`}>
        <div className={`${block}__form`}>
          <div className={`${block}__user`}>
            <CustomAvatar
              src={userDetails?.profilePic ?? undefined}
              name={userDetails?.name ?? undefined}
              size={48}
              border={0}
              className={`${block}__avatar`}
            />
            <div className={`${block}__user-info`}>
              <span className={`${block}__user-name`}>
                {userDetails?.name ?? 'Unknown'}
              </span>
              <div className={`${block}__badges`}>
                <span className={`${block}__tq-badge`}>
                  <TQConnectIcon size={14} className={`${block}__tq-icon`} />
                  {formattedScore} {scoreName}
                </span>
                {tqScore > 0 && (
                  <TQConnectBadge
                    score={tqScore}
                    iconSize="sm"
                    style={{ flexShrink: 0 }}
                  />
                )}
              </div>
            </div>
          </div>

          <label className={`${block}__label`}>How You Can Offer Help</label>
          <div
            className={`${block}__field ${block}__field--tappable`}
            role="button"
            tabIndex={0}
            onClick={() => setActiveStep('message')}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setActiveStep('message');
              }
            }}>
            <span
              className={`${block}__tappable-value ${block}__tappable-value--multiline ${
                !offerMessage.trim()
                  ? `${block}__tappable-value--placeholder`
                  : ''
              }`}>
              {offerMessage.trim()
                ? `${offerMessage.trim().slice(0, 80)}${offerMessage.length > 80 ? '...' : ''}`
                : PLACEHOLDER}
            </span>
          </div>

          <div className={`${block}__attachments`}>
            <AttachedFileButton
              attachedFiles={attachedFiles}
              setAttachedFiles={setAttachedFiles}
            />
          </div>

          {isUploading && (
            <div className={`${block}__upload-progress`}>
              <div className={`${block}__upload-progress-label`}>
                Uploading files... {Math.round(uploadProgress)}%
              </div>
              <div className={`${block}__upload-progress-track`}>
                <div
                  className={`${block}__upload-progress-fill`}
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className={`${block}__keyboard-spacer`} />
        </div>
      </div>
    </div>
  );
};

export default TQHelpOfferForm;
